import React, { useState } from 'react';
import {
  useGetEventsQuery,
  useDeleteEventMutation,
  useToggleEventJoinsMutation,
  Event,
} from '@/store/api/eventsApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { ConfirmationModal } from '@/components/common/ConfirmationModal';
import { Loader } from '@/components/common/Loader';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import { useDebounce } from '@/hooks/useDebounce';
import { useNotifications } from '@/hooks/useNotifications';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EventFormModal } from './components/EventFormModal';
import { EventsTable } from './components/EventsTable';

export function EventsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'Published' | 'Draft' | ''>('');
  const [timeFilter, setTimeFilter] = useState<'Upcoming' | 'Past' | 'Month' | 'Day' | ''>('Upcoming');
  const [take, setTake] = useState(10);
  const skip = (page - 1) * take;

  const {
    data: response,
    isLoading,
    error,
    isFetching,
  } = useGetEventsQuery({
    search: debouncedSearch || undefined,
    skip,
    take,
    status: statusFilter || undefined,
    timeFilter: timeFilter || undefined,
  });

  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();
  const [toggleEventJoins] = useToggleEventJoinsMutation();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState<Partial<Event> | null>(null);

  const [eventToDelete, setEventToDelete] = useState<{ id: number; title: string } | null>(null);
  const { success: notifySuccess, error: notifyError } = useNotifications();

  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, timeFilter, take]);

  const confirmDelete = async () => {
    if (!eventToDelete) return;
    try {
      await deleteEvent(eventToDelete.id).unwrap();
      notifySuccess('Event deleted.');
      setEventToDelete(null);
    } catch (err: any) {
      notifyError(err?.data?.message || 'Failed to delete event. Please try again.');
    }
  };

  const handleToggleJoins = async (id: number, currentStatus: boolean) => {
    try {
      await toggleEventJoins({
        id,
        acceptingJoins: !currentStatus,
      }).unwrap();
    } catch (err: any) {
      notifyError(err?.data?.message || 'Failed to update status.');
    }
  };

  const openAddModal = () => {
    setEventToEdit(null);
    setIsFormOpen(true);
  };

  const openEditModal = (event: Event) => {
    setEventToEdit(event);
    setIsFormOpen(true);
  };

  const openDuplicateModal = (event: Event) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, createdAt, updatedAt, _count, ...rest } = event as any;
    setEventToEdit({ ...rest, title: `${rest.title} (Copy)` } as Partial<Event>);
    setIsFormOpen(true);
  };

  const events = response?.data || [];
  const totalPages = response ? Math.ceil(response.count / take) : 0;

  if (isLoading && !events.length) {
    return <Loader />;
  }

  if (error) {
    return (
      <ErrorBanner
        type="error"
        title="Failed to load events"
        message="There was an error loading the events. Please try again."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Events</h1>
          <p className="text-muted-foreground">Manage upcoming and past events for the portal.</p>
        </div>
        <Button onClick={openAddModal}>
          <Plus className="mr-2 h-4 w-4" /> Add Event
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 pb-4">
          <div>
            <CardTitle>All Events</CardTitle>
            <CardDescription>{response?.count ?? 0} events total</CardDescription>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap items-center gap-2 mt-4 sm:mt-0 w-full sm:w-auto">
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value as any)}
              className="h-9 w-full sm:w-auto rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">All Time</option>
              <option value="Upcoming">Upcoming</option>
              <option value="Past">Past</option>
              <option value="Month">This Month</option>
              <option value="Day">Today</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'Published' | 'Draft' | '')}
              className="h-9 w-full sm:w-auto rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">All Statuses</option>
              <option value="Published">Published</option>
              <option value="Draft">Draft</option>
            </select>
            <select
              value={take}
              onChange={(e) => setTake(Number(e.target.value))}
              className="h-9 w-full sm:w-auto rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search title, description..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <EventsTable
            events={events}
            isFetching={isFetching}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            onEdit={openEditModal}
            onDuplicate={openDuplicateModal}
            onDelete={(e) => setEventToDelete({ id: e.id, title: e.title })}
            onToggleJoins={handleToggleJoins}
          />

          {response && response.count > 0 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Showing <span className="font-medium">{skip + 1}</span> to{' '}
                <span className="font-medium">{Math.min(skip + take, response.count)}</span> of{' '}
                <span className="font-medium">{response.count}</span> results
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
                <div className="text-sm font-medium">
                  Page {page} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <EventFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        eventToEdit={eventToEdit}
      />

      <ConfirmationModal
        isOpen={!!eventToDelete}
        onClose={() => setEventToDelete(null)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Delete Event"
        description={`Are you sure you want to delete "${eventToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
      />
    </div>
  );
}
